import type { AgeBand, SelectedProvider } from "../../pages/Screener";
import type { ScreenerResult } from "./scoring";
import { PSC_TOTAL_MAX } from "./scoring";

export const SCREENER_UTM_MEDIUM = "pediatrician";
export const SCREENER_UTM_CAMPAIGN = "pedscreener";
export const SCREENER_REVIEW_CHANNEL = "PEDSCREENER_REVIEW";

export type Contact = {
  readonly firstName: string;
  readonly lastName: string;
  readonly email?: string;
  readonly phoneNumber?: string;
};

type ReferredClientInput = {
  readonly type: "CHILD" | "CAREGIVER";
  readonly isCareRecipient: boolean;
  readonly contact: Contact;
};

export type CreateReferralInput = {
  readonly externalOrganizationId: number;
  readonly externalProviderId: number;
  readonly utmSource: string;
  readonly utmMedium: string;
  readonly utmCampaign: string;
  readonly note: string;
  readonly referredClients: readonly ReferredClientInput[];
  readonly referralChannel?: string;
  readonly disableTrackerUpdate?: boolean;
};

function trimmedContact(contact: Contact): Contact {
  return {
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    ...(contact.email?.trim() ? { email: contact.email.trim() } : {}),
    ...(contact.phoneNumber?.trim() ? { phoneNumber: contact.phoneNumber.trim() } : {})
  };
}

export function buildScreenerSummary(result: ScreenerResult, ageBand: AgeBand): string {
  const subscales = result.subscales
    .map(subscale => `${subscale.label} ${subscale.score} (${subscale.state})`)
    .join("; ");
  return [
    `PSC-17 total ${result.total}/${PSC_TOTAL_MAX} — ${result.headline}.`,
    subscales + ".",
    `Caregiver wellbeing: ${result.caregiverIndicator}.`,
    `Child age band ${ageBand}.`
  ].join(" ");
}

export function buildConnectToCareReferral(
  provider: SelectedProvider,
  result: ScreenerResult,
  ageBand: AgeBand,
  caregiver: Contact
): CreateReferralInput {
  const caregiverContact = trimmedContact(caregiver);
  return {
    externalOrganizationId: provider.externalOrganizationId,
    externalProviderId: provider.externalProviderId,
    utmSource: provider.providerName,
    utmMedium: SCREENER_UTM_MEDIUM,
    utmCampaign: SCREENER_UTM_CAMPAIGN,
    note: buildScreenerSummary(result, ageBand),
    referredClients: [
      {
        type: "CHILD",
        isCareRecipient: true,
        contact: { firstName: caregiverContact.firstName, lastName: caregiverContact.lastName }
      },
      {
        type: "CAREGIVER",
        isCareRecipient: false,
        contact: caregiverContact
      }
    ]
  };
}

export function buildReviewReferral(
  provider: SelectedProvider,
  result: ScreenerResult,
  ageBand: AgeBand,
  child: Contact
): CreateReferralInput {
  return {
    externalOrganizationId: provider.externalOrganizationId,
    externalProviderId: provider.externalProviderId,
    utmSource: provider.providerName,
    utmMedium: SCREENER_UTM_MEDIUM,
    utmCampaign: SCREENER_UTM_CAMPAIGN,
    note: buildScreenerSummary(result, ageBand),
    referralChannel: SCREENER_REVIEW_CHANNEL,
    disableTrackerUpdate: true,
    referredClients: [
      {
        type: "CHILD",
        isCareRecipient: true,
        contact: trimmedContact(child)
      }
    ]
  };
}
